import React from "react";
import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import { Shield } from 'lucide-react';

const TermCondition = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header with Logo */}
          <div className="bg-emerald-600 px-6 py-4">
            <div className="flex items-center space-x-3">
              <Shield className="h-8 w-8 text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white">BREMSKERL</h1>
                <p className="text-emerald-100 text-sm">Terms and Privacy Policy</p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="px-6 py-8">
            <div className="prose max-w-none">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Privacy Policy</h2>
                <p className="text-sm text-gray-500">Last updated: February 10, 2025</p>
              </div>

              <div className="space-y-6 text-gray-600">
                <p className="leading-relaxed">
                  This Privacy Policy describes Our policies and procedures on the collection,
                  use and disclosure of Your information when You use the Service and tells
                  You about Your privacy rights and how the law protects You.
                </p>

                <p className="leading-relaxed">
                  We use Your Personal data to provide and improve the Service. By using the
                  Service, You agree to the collection and use of information in accordance
                  with this Privacy Policy.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
                  Interpretation and Definitions
                </h3>

                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Interpretation</h4>
                  <p className="leading-relaxed">
                    The words of which the initial letter is capitalized have meanings defined
                    under the following conditions. The following definitions shall have the
                    same meaning regardless of whether they appear in singular or in plural.
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900">Definitions</h4>
                  <p>For the purposes of this Privacy Policy:</p>
                  <ul className="list-disc pl-6 space-y-3">
                    <li>
                      <p><span className="font-medium">Account</span> means a unique account created
                      for You to access our Service or parts of our Service.</p>
                    </li>
                    <li>
                      <p><span className="font-medium">Affiliate</span> means an entity that
                      controls, is controlled by or is under common control with a party,
                      where "control" means ownership of 50% or more of the shares, equity
                      interest or other securities entitled to vote for election of directors
                      or other managing authority.</p>
                    </li>
                    <li>
                      <p><span className="font-medium">Application</span> refers to BREMSKERL AOT,
                      the software program provided by the Company.</p>
                    </li>
                    <li>
                      <p><span className="font-medium">Company</span> (referred to as either
                      "the Company", "We", "Us" or "Our" in this Agreement) refers to T7E
                      Aftermarket Connect Private Limited, 135, Avior Corporate Park, LBS
                      Marg, Mulund (W) 400080, Mumbai, Maharashtra.</p>
                    </li>
                    <li>
                      <p><span className="font-medium">Country</span> refers to: Maharashtra,
                      India</p>
                    </li>
                    <li>
                      <p><span className="font-medium">Device</span> means any device that can
                      access the Service such as a computer, a cellphone or a digital
                      tablet.</p>
                    </li>
                    <li>
                      <p><span className="font-medium">Personal Data</span> is any information
                      that relates to an identified or identifiable individual.</p>
                    </li>
                    <li>
                      <p><span className="font-medium">Service</span> refers to the
                      Application.</p>
                    </li>
                    <li>
                      <p><span className="font-medium">Service Provider</span> means any natural
                      or legal person who processes the data on behalf of the Company. It
                      refers to third-party companies or individuals employed by the Company
                      to facilitate the Service, to provide the Service on behalf of the
                      Company, to perform services related to the Service or to assist the
                      Company in analyzing how the Service is used.</p>
                    </li>
                    <li>
                      <p><span className="font-medium">Usage Data</span> refers to data
                      collected automatically, either generated by the use of the Service or
                      from the Service infrastructure itself (for example, the duration of a
                      page visit).</p>
                    </li>
                    <li>
                      <p><span className="font-medium">You</span> means the individual accessing
                      or using the Service, or the company, or other legal entity on behalf
                      of which such individual is accessing or using the Service, as
                      applicable.</p>
                    </li>
                  </ul>
                </div>

                <div className="mt-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Collecting and Using Your Personal Data
                  </h3>
                  
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">
                      Types of Data Collected
                    </h4>
                    
                    <div className="space-y-6">
                      <div>
                        <h5 className="text-base font-medium text-gray-900 mb-3">Personal Data</h5>
                        <p className="mb-3">
                          While using Our Service, We may ask You to provide Us with certain
                          personally identifiable information that can be used to contact or
                          identify You. Personally identifiable information may include, but is
                          not limited to:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                          <li>First name and last name</li>
                          <li>Phone number</li>
                          <li>Address, State, Province, ZIP/Postal code, City</li>
                          <li>Usage Data</li>
                        </ul>
                      </div>

                      <div>
                        <h5 className="text-base font-medium text-gray-900 mb-3">Usage Data</h5>
                        <p className="mb-3">
                          Usage Data is collected automatically when using the Service.
                        </p>
                        <p className="mb-3">
                          Usage Data may include information such as Your Device's Internet
                          Protocol address (e.g. IP address), browser type, browser version,
                          the pages of our Service that You visit, the time and date of Your
                          visit, the time spent on those pages, unique device identifiers
                          and other diagnostic data.
                        </p>
                        <p className="mb-3">
                          When You access the Service by or through a mobile device, We may
                          collect certain information automatically, including, but not
                          limited to, the type of mobile device You use, Your mobile device
                          unique ID, the IP address of Your mobile device, Your mobile
                          operating system, the type of mobile Internet browser You use,
                          unique device identifiers and other diagnostic data.
                        </p>
                      </div>

                      <div>
                        <h5 className="text-base font-medium text-gray-900 mb-3">
                          Information Collected while Using the Application
                        </h5>
                        <p className="mb-3">
                          While using Our Application, in order to provide features of Our
                          Application, We may collect, with Your prior permission:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                          <li>
                            Pictures and other information from your Device's camera and
                            photo library
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h5 className="text-base font-medium text-gray-900 mb-3">
                          Sensitive Information
                        </h5>
                        <p className="mb-3">
                          Geolocation Information. We may request access or permission to
                          track location-based information from your mobile device, either
                          continuously or while you are using our mobile application(s), to
                          provide certain location-based services. If you wish to change our
                          access or permissions, you may do so in your device's settings. We
                          need this permission to continuous track your location and
                          calculate the path and distance covered on the basis of which the
                          salaries are paid.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h4 className="text-lg font-medium text-gray-900">Use of Your Personal Data</h4>
                    <p>The Company may use Personal Data for the following purposes:</p>
                    <ul className="list-disc pl-6 space-y-3">
                      <li>
                        <p><span className="font-medium">To provide and maintain our Service</span>,
                        including to monitor the usage of our Service.</p>
                      </li>
                      <li>
                        <p><span className="font-medium">To manage Your Account:</span> to manage
                        Your registration as a user of the Service. The Personal Data You
                        provide can give You access to different functionalities of the
                        Service that are available to You as a registered user.</p>
                      </li>
                      <li>
                        <p><span className="font-medium">For the performance of a contract:</span>
                        the development, compliance and undertaking of the purchase contract
                        for the products, items or services You have purchased or of any
                        other contract with Us through the Service.</p>
                      </li>
                      <li>
                        <p><span className="font-medium">To contact You:</span> To contact You by
                        email, telephone calls, SMS, or other equivalent forms of electronic
                        communication, such as a mobile application's push notifications
                        regarding updates or informative communications related to the
                        functionalities, products or contracted services, including the
                        security updates, when necessary or reasonable for their
                        implementation.</p>
                      </li>
                      <li>
                        <p><span className="font-medium">To provide You</span> with news, special
                        offers and general information about other goods, services and
                        events which we offer that are similar to those that you have
                        already purchased or enquired about unless You have opted not to
                        receive such information.</p>
                      </li>
                      <li>
                        <p><span className="font-medium">To manage Your requests:</span> To attend
                        and manage Your requests to Us.</p>
                      </li>
                      <li>
                        <p><span className="font-medium">For business transfers:</span> We may use
                        Your information to evaluate or conduct a merger, divestiture,
                        restructuring, reorganization, dissolution, or other sale or
                        transfer of some or all of Our assets, whether as a going concern
                        or as part of bankruptcy, liquidation, or similar proceeding, in
                        which Personal Data held by Us about our Service users is among the
                        assets transferred.</p>
                      </li>
                      <li>
                        <p><span className="font-medium">For other purposes:</span> We may use
                        Your information for other purposes, such as data analysis,
                        identifying usage trends, determining the effectiveness of our
                        promotional campaigns and to evaluate and improve our Service,
                        products, services, marketing and your experience.</p>
                      </li>
                    </ul>
                  </div>

                  <div className="mt-8 space-y-6">
                    <h4 className="text-lg font-medium text-gray-900">Sharing Your Personal Information</h4>
                    <p>We may share Your personal information in the following situations:</p>
                    <ul className="list-disc pl-6 space-y-3">
                      <li>
                        <span className="font-medium">With Service Providers:</span> We may
                        share Your personal information with Service Providers to monitor
                        and analyze the use of our Service, to contact You.
                      </li>
                      <li>
                        <span className="font-medium">For business transfers:</span> We may
                        share or transfer Your personal information in connection with, or
                        during negotiations of, any merger, sale of Company assets,
                        financing, or acquisition of all or a portion of Our business to
                        another company.
                      </li>
                      <li>
                        <span className="font-medium">With Affiliates:</span> We may share
                        Your information with Our affiliates, in which case we will require
                        those affiliates to honor this Privacy Policy.
                      </li>
                      <li>
                        <span className="font-medium">With business partners:</span> We may
                        share Your information with Our business partners to offer You
                        certain products, services or promotions.
                      </li>
                      <li>
                        <span className="font-medium">With other users:</span> when You
                        share personal information or otherwise interact in the public
                        areas with other users, such information may be viewed by all
                        users and may be publicly distributed outside.
                      </li>
                      <li>
                        <span className="font-medium">With Your consent:</span> We may
                        disclose Your personal information for any other purpose with Your
                        consent.
                      </li>
                    </ul>
                  </div>

                  <div className="mt-8 space-y-6">
                    <h3 className="text-xl font-semibold text-gray-900">Security and Data Retention</h3>
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">
                        Retention of Your Personal Data
                      </h4>
                      <p className="mb-4">
                        The Company will retain Your Personal Data only for as long as is
                        necessary for the purposes set out in this Privacy Policy. We will
                        retain and use Your Personal Data to the extent necessary to comply
                        with our legal obligations, resolve disputes, and enforce our legal
                        agreements and policies.
                      </p>
                      <p>
                        The Company will also retain Usage Data for internal analysis
                        purposes. Usage Data is generally retained for a shorter period of
                        time, except when this data is used to strengthen the security or
                        to improve the functionality of Our Service, or We are legally
                        obligated to retain this data for longer time periods.
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 space-y-6">
                    <h3 className="text-xl font-semibold text-gray-900">Children's Privacy</h3>
                    <p>
                      Our Service does not address anyone under the age of 13. We do not
                      knowingly collect personally identifiable information from anyone
                      under the age of 13. If You are a parent or guardian and You are
                      aware that Your child has provided Us with Personal Data, please
                      contact Us.
                    </p>
                  </div>

                  <div className="mt-8 space-y-6">
                    <h3 className="text-xl font-semibold text-gray-900">Contact Us</h3>
                    <p>
                      If you have any questions about this Privacy Policy, You can contact
                      us:
                    </p>
                    <ul className="list-disc pl-6">
                      <li>
                        By visiting this page on our website:{" "}
                        <a
                          href="http://devBREMSKERL.t7e.in/support"
                          className="text-emerald-600 hover:text-emerald-700"
                          rel="external nofollow noopener"
                          target="_blank"
                        >
                          http://devBREMSKERL.t7e.in/support
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 mt-8">
            <p className="text-sm text-gray-500 text-center">
              © 2025 BREMSKERL AOT. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermCondition;